"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useCartContext } from "../context/CartContext";
import { useUser, SignInButton } from "@clerk/nextjs";
import { Trash2, ShoppingBag, Plus, Minus, Lock, ShieldCheck, ArrowLeft } from "lucide-react";

interface CartItem {
  _id: string;
  title: string;
  price: number;
  quantity: number;
  imageUrl: string;
}

const Carts: React.FC = () => {
  const { cart, updateQuantity, removeFromCart, cartTotal } = useCartContext();
  const { isSignedIn, isLoaded } = useUser();
  const [shippingSettings, setShippingSettings] = useState<any>(null);
  const [orderNote, setOrderNote] = useState<string>("");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch('/api/shipping/settings');
        const data = await res.json();
        if (data.settings) {
          setShippingSettings(data.settings);
        }
      } catch (err) {
        console.error("Failed to fetch shipping settings", err);
      }
    };
    fetchSettings();
  }, []);

  const exportToExcel = () => {
    if (cart.length === 0) return;

    const formattedDate = new Date().toLocaleDateString("en-PK", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });

    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8" />
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Shopping Cart Estimate</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; }
          .title { font-size: 18pt; font-weight: bold; color: #B88E2F; text-align: center; }
          .meta-info { font-size: 10pt; color: #555555; }
          th { font-size: 11pt; font-weight: bold; background-color: #B88E2F; color: #ffffff; border: 1px solid #d4b26f; padding: 12px 10px; text-align: left; }
          td { border: 1px solid #e5e7eb; padding: 10px 8px; vertical-align: middle; }
          .price { text-align: right; mso-number-format: '"Rs. "##,##0'; }
          .quantity { text-align: center; mso-number-format: '0'; }
          .total-label { font-weight: bold; background-color: #FAF7F2; text-align: right; border-top: 2px double #B88E2F; font-size: 11pt; }
          .total-value { font-weight: bold; background-color: #FAF7F2; text-align: right; border-top: 2px double #B88E2F; mso-number-format: '"Rs. "##,##0'; font-size: 11pt; }
        </style>
      </head>
      <body>
        <table>
          <tr>
            <td colspan="5" class="title" style="padding: 15px 0;">MD BROTHERS EDU</td>
          </tr>
          <tr>
            <td colspan="5" class="meta-info"><strong>Document:</strong> Shopping Cart Estimate Product List</td>
          </tr>
          <tr>
            <td colspan="5" class="meta-info"><strong>Generated Date:</strong> ${formattedDate}</td>
          </tr>
          <tr>
            <td colspan="5" style="height: 10px;"></td>
          </tr>
          <thead>
            <tr>
              <th style="width: 50px; text-align: center;">No.</th>
              <th style="width: 350px;">Product Name</th>
              <th style="width: 120px; text-align: right;">Unit Price</th>
              <th style="width: 80px; text-align: center;">Quantity</th>
              <th style="width: 150px; text-align: right;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
    `;

    cart.forEach((item: any, idx: number) => {
      html += `
        <tr>
          <td style="text-align: center; font-weight: bold; color: #666666;">${idx + 1}</td>
          <td style="font-weight: bold; color: #111111;">${item.title}</td>
          <td class="price">${item.price}</td>
          <td class="quantity">${item.quantity}</td>
          <td class="price" style="font-weight: bold; color: #111111;">${item.price * item.quantity}</td>
        </tr>
      `;
    });

    html += `
          </tbody>
          <tfoot>
            <tr>
              <td colspan="4" class="total-label" style="padding: 12px 10px;">Grand Total</td>
              <td class="total-value" style="color: #B88E2F; padding: 12px 10px;">${cartTotal}</td>
            </tr>
          </tfoot>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `md_brothers_cart_estimate_${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        
        {/* Cart Items Section */}
        <div className="w-full lg:w-2/3 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingBag className="w-6 h-6 text-[#B88E2F]" />
              Shopping Cart
            </h2>
            <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {cart.length} {cart.length === 1 ? "Item" : "Items"}
            </span>
          </div>

          {cart.length > 0 ? (
            <div className="space-y-6">
              {/* Table Headers (hidden on mobile) */}
              <div className="hidden md:grid grid-cols-12 gap-4 font-semibold text-xs uppercase tracking-wider text-gray-400 bg-gray-50/50 py-3 px-4 rounded-lg">
                <span className="col-span-6">Product Details</span>
                <span className="col-span-2 text-center">Price</span>
                <span className="col-span-2 text-center">Quantity</span>
                <span className="col-span-2 text-right">Total</span>
              </div>

              {/* Cart Items List */}
              <div className="divide-y divide-gray-100">
                {cart.map((item: CartItem) => (
                  <div
                    key={item._id}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center py-5 first:pt-0 last:pb-0"
                  >
                    {/* Product Detail (Image & Name) */}
                    <div className="col-span-1 md:col-span-6 flex items-center gap-4">
                      <div className="relative w-16 h-16 rounded-xl border border-gray-100 overflow-hidden bg-gray-50 flex-shrink-0">
                        <Image
                          src={item.imageUrl}
                          alt={item.title}
                          fill
                          className="object-cover"
                          sizes="64px"
                        />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900 text-base truncate hover:text-[#B88E2F] transition-colors">
                          {item.title}
                        </h4>
                        <button
                          onClick={() => removeFromCart(item._id)}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors mt-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Remove
                        </button>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="col-span-1 md:col-span-2 text-left md:text-center flex md:block justify-between items-center">
                      <span className="md:hidden text-xs font-semibold text-gray-400 uppercase">Price</span>
                      <p className="font-semibold text-gray-700">Rs. {item.price.toLocaleString()}</p>
                    </div>

                    {/* Quantity Selector */}
                    <div className="col-span-1 md:col-span-2 flex md:justify-center items-center justify-between">
                      <span className="md:hidden text-xs font-semibold text-gray-400 uppercase">Quantity</span>
                      <div className="flex items-center border border-gray-200 rounded-lg bg-gray-50 p-1">
                        <button
                          onClick={() => updateQuantity(item._id, Math.max(1, item.quantity - 1))}
                          className="p-1 hover:bg-white hover:text-[#B88E2F] rounded-md transition-all text-gray-500"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (!isNaN(val) && val >= 1) updateQuantity(item._id, val);
                          }}
                          className="w-10 bg-transparent text-center text-sm font-semibold text-gray-800 border-none focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button
                          onClick={() => updateQuantity(item._id, item.quantity + 1)}
                          className="p-1 hover:bg-white hover:text-[#B88E2F] rounded-md transition-all text-gray-500"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Total & Action */}
                    <div className="col-span-1 md:col-span-2 text-right flex md:block justify-between items-center">
                      <span className="md:hidden text-xs font-semibold text-gray-400 uppercase">Subtotal</span>
                      <p className="font-bold text-gray-900 text-base">
                        Rs. {(item.price * item.quantity).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Notes / Instructions */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <label htmlFor="order-note" className="block text-sm font-semibold text-gray-700 mb-2">
                  Special Instructions / Order Notes
                </label>
                <textarea
                  id="order-note"
                  rows={3}
                  value={orderNote}
                  onChange={(e) => setOrderNote(e.target.value)}
                  placeholder="E.g., Special delivery instructions, custom packaging details..."
                  className="w-full rounded-xl border border-gray-200 p-3.5 text-sm focus:border-[#B88E2F] focus:outline-none focus:ring-1 focus:ring-[#B88E2F] transition-all bg-gray-50/50"
                />
              </div>

              <div className="flex justify-between items-center pt-6 border-t border-gray-100">
                <Link
                  href="/shop"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#B88E2F] hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Continue Shopping
                </Link>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-[#F9F1E7] rounded-full flex items-center justify-center mb-4">
                <ShoppingBag className="w-8 h-8 text-[#B88E2F]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Your cart is empty</h3>
              <p className="text-gray-500 text-sm max-w-sm mb-6">
                Looks like you haven't added anything to your cart yet. Let's find some amazing school & office supplies!
              </p>
              <Link
                href="/shop"
                className="bg-[#B88E2F] hover:bg-[#a37d2a] text-white font-bold py-3 px-8 rounded-xl transition shadow-lg hover:shadow-xl hover:translate-y-[-2px] transform transition-transform duration-200 text-sm"
              >
                Explore Shop
              </Link>
            </div>
          )}
        </div>

        {/* Cart Summary Panel */}
        <div className="w-full lg:w-1/3 bg-[#F9F1E7] p-6 rounded-2xl border border-[#B88E2F]/10">
          <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b border-[#B88E2F]/15">
            Order Summary
          </h3>

          {/* Shipping Progress Threshold */}
          {shippingSettings?.freeShippingThreshold && cart.length > 0 && (
            <div className="mb-6 p-4 bg-white/70 backdrop-blur rounded-xl border border-[#B88E2F]/15">
              {cartTotal >= shippingSettings.freeShippingThreshold ? (
                <p className="text-green-600 font-bold text-center text-sm flex items-center justify-center gap-1.5 animate-bounce">
                  <span>🎉</span> Your order qualifies for FREE shipping!
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-slate-700 text-xs font-semibold">
                    Add <span className="text-[#B88E2F] font-bold">Rs. {(shippingSettings.freeShippingThreshold - cartTotal).toLocaleString()}</span> more for <span className="font-bold">FREE Shipping</span>
                  </p>
                  <div className="w-full bg-white h-2 rounded-full overflow-hidden border border-gray-100">
                    <div 
                      className="bg-[#B88E2F] h-full transition-all duration-500" 
                      style={{ width: `${Math.min(100, (cartTotal / shippingSettings.freeShippingThreshold) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pricing Breakdowns */}
          <div className="space-y-4">
            <div className="flex justify-between items-center text-gray-600 text-sm">
              <span>Subtotal</span>
              <span className="font-semibold text-gray-900">Rs. {cartTotal.toLocaleString()}</span>
            </div>
            
            <div className="flex justify-between items-center text-gray-600 text-sm">
              <span>Shipping Cost</span>
              <span className="font-semibold text-gray-900">
                {shippingSettings && cartTotal >= shippingSettings.freeShippingThreshold ? (
                  <span className="text-green-600 font-bold">FREE</span>
                ) : (
                  "Calculated at checkout"
                )}
              </span>
            </div>
            
            <div className="border-t border-[#B88E2F]/15 pt-4 flex justify-between items-center">
              <span className="text-base font-bold text-gray-900">Total</span>
              <div className="text-right">
                <span className="text-xl font-bold text-[#B88E2F]">
                  Rs. {cartTotal.toLocaleString()}
                </span>
                <p className="text-[10px] text-gray-400 mt-0.5">Inclusive of taxes & duties</p>
              </div>
            </div>
          </div>

          {/* Export to Excel CTA */}
          {cart.length > 0 && (
            <div className="mt-6 mb-4">
              <button 
                onClick={exportToExcel}
                className="w-full bg-white hover:bg-gray-50 text-[#B88E2F] border-2 border-[#B88E2F] font-bold py-3.5 px-6 rounded-xl transition shadow-sm hover:shadow-md flex items-center justify-center gap-2 text-sm"
              >
                <svg className="w-5 h-5 text-[#B88E2F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Estimate (Excel)
              </button>
            </div>
          )}

          {/* Checkout CTA Buttons */}
          <div className="mt-2">
            {cart.length > 0 ? (
              isLoaded && isSignedIn ? (
                <Link href="/checkout" className="block w-full">
                  <button className="w-full bg-[#B88E2F] hover:bg-[#a37d2a] text-white font-bold py-3.5 px-6 rounded-xl transition shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-base">
                    <Lock className="w-4 h-4" />
                    Proceed to Secure Checkout
                  </button>
                </Link>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500 text-center">
                    Please sign in to complete your checkout process.
                  </p>
                  <SignInButton mode="modal" forceRedirectUrl="/checkout">
                    <button className="w-full bg-[#B88E2F] hover:bg-[#a37d2a] text-white font-bold py-3.5 px-6 rounded-xl transition shadow-md flex items-center justify-center gap-2 text-base">
                      Sign In to Checkout
                    </button>
                  </SignInButton>
                </div>
              )
            ) : (
              <button 
                disabled 
                className="w-full bg-gray-300 text-gray-500 font-semibold py-3.5 px-6 rounded-xl cursor-not-allowed text-base"
              >
                Proceed to Checkout
              </button>
            )}
          </div>

          {/* Trust Guarantees */}
          <div className="mt-6 pt-6 border-t border-[#B88E2F]/15 space-y-3">
            <div className="flex items-center gap-2.5 text-xs text-gray-600">
              <ShieldCheck className="w-4.5 h-4.5 text-[#B88E2F]" />
              <span>Safe & Secure Checkout Guaranteed</span>
            </div>
            <div className="flex items-center gap-2.5 text-xs text-gray-600">
              <svg className="w-4.5 h-4.5 text-[#B88E2F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Easy Claims & 7 Days Replacements</span>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default Carts;
